/**
 * PendingOrdersModal.js
 *
 * Resolution screen – lists all Pending / Failed orders.
 * Allows users to "Retry All" or "View Details" of individual orders.
 */

import React, {memo, useCallback, useMemo} from 'react';
import {Alert, FlatList, Modal, Platform, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions,} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import {useTranslation} from 'react-i18next';
import i18n from '../../i18n';
import {WINDOW_MEDIUM_MIN, getModalWidth} from '../helpers/windowSizeClass';

const STATUS_COLORS = {
  Pending: '#f0ad4e',
  Syncing: '#5bc0de',
  Failed: '#dc3545',
};

const STATUS_LABELS = {
  Pending: 'Pending',
  Syncing: 'Syncing…',
  Failed: 'Failed',
};

// ── Single order row ────────────────────────────────────────────────────────
const OrderRow = memo(({ item, onRetry, onRemove }) => {
  const { t } = useTranslation();
  const STATUS_LABEL_KEYS = {
    Pending: 'pending_orders_status_pending',
    Syncing: 'pending_orders_status_syncing',
    Failed: 'pending_orders_status_failed',
  };
  const orderData = item.order_data || {};
  const itemCount = (orderData.cartItems?.length || 0) + (orderData.alreadySentCartItems?.length || 0);

  const handleRetry = useCallback(() => onRetry(item.id), [item.id, onRetry]);

  const handleRemove = useCallback(() => {
    if (Platform.OS === 'web') {
      if (window.confirm(t('remove_order_confirm'))) {
        onRemove(item.id);
      }
    } else {
      Alert.alert(
        i18n.t('remove_order_title'),
        i18n.t('remove_order_confirm'),
        [
          { text: t('cancel'), style: 'cancel' },
          { text: t('pending_orders_remove'), style: 'destructive', onPress: () => onRemove(item.id) },
        ],
      );
    }
  }, [item.id, onRemove, t]);

  const statusColor = STATUS_COLORS[item.sync_status] || '#999';
  const timestamp = item.timestamp
    ? new Date(item.timestamp).toLocaleString()
    : '—';

  return (
    <View style={styles.row}>
      {/* Status indicator */}
      <View style={[styles.statusDot, { backgroundColor: statusColor }]} />

      {/* Info */}
      <View style={styles.rowInfo}>
        <Text style={styles.rowTitle} numberOfLines={1}>
          {t('pending_orders_order_number', {id: item.id?.substring(0, 8)})}
        </Text>
        <Text style={styles.rowSub}>
          {STATUS_LABEL_KEYS[item.sync_status] ? t(STATUS_LABEL_KEYS[item.sync_status]) : item.sync_status}
          {'  •  '}
          {t('pending_orders_item_count', {count: itemCount})}
          {'  •  '}
          {orderData.cartTotal != null ? `${Math.round(orderData.cartTotal)} Ft` : ''}
        </Text>
        <Text style={styles.rowTimestamp}>{timestamp}</Text>
        {item.error_message ? (
          <Text style={styles.rowError} numberOfLines={2}>
            {t('error')}: {item.error_message}
          </Text>
        ) : null}
        {item.retry_count > 0 && (
          <Text style={styles.rowRetry}>{t('pending_orders_retries', {count: item.retry_count})}</Text>
        )}
      </View>

      {/* Actions */}
      <View style={styles.rowActions}>
        {item.sync_status === 'Failed' && (
          <TouchableOpacity style={styles.retryBtn} onPress={handleRetry}>
            <Icon name="refresh-cw" size={16} color="#fff" />
          </TouchableOpacity>
        )}
        <TouchableOpacity style={styles.removeBtn} onPress={handleRemove}>
          <Icon name="trash-2" size={16} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );
});

// ── Modal ────────────────────────────────────────────────────────────────────
const PendingOrdersModal = ({
  visible,
  onClose,
  orders = [],
  onRetryAll,
  onRetryOne,
  onRemoveOrder,
  isOnline,
  pendingCount = 0,
  failedCount = 0,
  syncingCount = 0,
}) => {
  const { t } = useTranslation();
  const { width, height } = useWindowDimensions();
  // Phones keep 92% of the window; 600dp+ gets a capped, centred card.
  const modalSize = useMemo(() => ({
    width: width >= WINDOW_MEDIUM_MIN ? getModalWidth(width, { max: 560, ratio: 0.8 }) : width * 0.92,
    maxHeight: height * 0.85,
  }), [width, height]);
  const handleRetryAll = useCallback(async () => {
    await onRetryAll();
  }, [onRetryAll]);

  const renderItem = useCallback(({ item }) => (
    <OrderRow item={item} onRetry={onRetryOne} onRemove={onRemoveOrder} />
  ), [onRetryOne, onRemoveOrder]);

  const keyExtractor = useCallback((item) => item.id, []);

  const ListHeader = useMemo(() => (
    <View style={styles.summaryRow}>
      <View style={styles.summaryItem}>
        <Text style={[styles.summaryCount, { color: STATUS_COLORS.Pending }]}>{pendingCount}</Text>
        <Text style={styles.summaryLabel}>{t('pending_orders_status_pending')}</Text>
      </View>
      <View style={styles.summaryItem}>
        <Text style={[styles.summaryCount, { color: STATUS_COLORS.Syncing }]}>{syncingCount}</Text>
        <Text style={styles.summaryLabel}>{t('pending_orders_summary_syncing')}</Text>
      </View>
      <View style={styles.summaryItem}>
        <Text style={[styles.summaryCount, { color: STATUS_COLORS.Failed }]}>{failedCount}</Text>
        <Text style={styles.summaryLabel}>{t('pending_orders_status_failed')}</Text>
      </View>
    </View>
  ), [pendingCount, syncingCount, failedCount, t]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.backdrop}>
        <View style={[styles.modal, modalSize]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{t('pending_orders_title')}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Icon name="x" size={24} color="#333" />
            </TouchableOpacity>
          </View>

          {/* Network indicator */}
          <View style={[styles.networkBar, { backgroundColor: isOnline ? '#28a745' : '#dc3545' }]}>
            <Text style={styles.networkText}>
              {isOnline ? t('pending_orders_online') : t('pending_orders_offline')}
            </Text>
          </View>

          {/* Summary */}
          {ListHeader}

          {/* List */}
          {orders.length === 0 ? (
            <View style={styles.emptyState}>
              <Icon name="check-circle" size={48} color="#28a745" />
              <Text style={styles.emptyText}>{t('pending_orders_all_synced')}</Text>
            </View>
          ) : (
            <FlatList
              data={orders}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              contentContainerStyle={styles.list}
              showsVerticalScrollIndicator={false}
              removeClippedSubviews={Platform.OS === 'android'}
              initialNumToRender={8}
              maxToRenderPerBatch={8}
              updateCellsBatchingPeriod={50}
              windowSize={7}
            />
          )}

          {/* Footer actions */}
          {failedCount > 0 && (
            <View style={styles.footer}>
              <TouchableOpacity style={styles.retryAllBtn} onPress={handleRetryAll}>
                <Icon name="refresh-cw" size={16} color="#fff" style={{ marginRight: 6 }} />
                <Text style={styles.retryAllText}>{t('pending_orders_retry_all_failed', {count: failedCount})}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#ddd',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#333',
  },
  networkBar: {
    paddingVertical: 4,
    alignItems: 'center',
  },
  networkText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#eee',
  },
  summaryItem: {
    alignItems: 'center',
  },
  summaryCount: {
    fontSize: 22,
    fontWeight: '800',
  },
  summaryLabel: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
  list: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 10,
  },
  rowInfo: {
    flex: 1,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  rowSub: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  rowTimestamp: {
    fontSize: 11,
    color: '#999',
    marginTop: 2,
  },
  rowError: {
    fontSize: 11,
    color: '#dc3545',
    marginTop: 4,
  },
  rowRetry: {
    fontSize: 11,
    color: '#f0ad4e',
    marginTop: 2,
  },
  rowActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 8,
  },
  retryBtn: {
    backgroundColor: '#f0ad4e',
    padding: 8,
    borderRadius: 8,
    marginRight: 6,
  },
  removeBtn: {
    backgroundColor: '#999',
    padding: 8,
    borderRadius: 8,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
    color: '#28a745',
    marginTop: 12,
    fontWeight: '600',
  },
  footer: {
    padding: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#ddd',
    alignItems: 'center',
  },
  retryAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0ad4e',
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 10,
  },
  retryAllText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
});

export default memo(PendingOrdersModal);

